import React, { useRef, useState } from 'react';

import Clickable from './Clickable';
import Layout, { Props as LayoutProps } from './Layout';
import Text from './Text';
import { useOnClickOutside } from './helpers';

interface Props {
  children: React.ReactNode | ((props: { isOpen: boolean }) => React.ReactNode);
  options: MenuableOption[];
  containerProps?: LayoutProps;
  menuProps?: LayoutProps;
  /** Text color for menu option labels. */
  itemTextColor?: React.ComponentProps<typeof Text>['color'];
  /** When set, menu open state is controlled by the parent. */
  isOpen?: boolean;
  /** Called when menu open state should change (open/close). Use with isOpen for controlled mode. */
  onOpenChange?: (open: boolean) => void;
}

interface MenuableOption {
  label: React.ReactNode;
  onClick: VoidFunction;
  itemProps?: LayoutProps;
}

const Menuable: React.FC<Props> = ({
  children,
  options,
  menuProps,
  itemTextColor,
  isOpen: isOpenProp,
  onOpenChange,
  ...containerProps
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState<number>();

  const isControlled = isOpenProp !== undefined;
  const isOpen = isControlled ? isOpenProp : internalOpen;
  const setOpen = (open: boolean) => {
    if (isControlled) {
      onOpenChange?.(open);
    } else {
      setInternalOpen(open);
    }
  };

  useOnClickOutside(containerRef, () => {
    setOpen(false);
  });

  const triggerContent = typeof children === 'function' ? children({ isOpen }) : children;

  return (
    <Layout
      position="relative"
      zIndex={isOpen ? 1000 : undefined}
      __ref={containerRef}
      onMeasure={() => setTop((containerRef.current?.getBoundingClientRect().height || 0) + 8)}
      {...containerProps}
    >
      <Clickable onClick={() => setOpen(!isOpen)}>{triggerContent}</Clickable>
      {isOpen && (
        <Layout
          position="absolute"
          top={top}
          right={0}
          color="white"
          borderRadius={12}
          zIndex={2}
          boxShadow="2px 0px 22px rgba(87, 73, 109, 0.12)"
          {...menuProps}
          __style={{ animation: 'none', transition: 'none' }}
        >
          {options.map((option, index) => {
            const isFirst = index === 0;
            const isLast = index === options.length - 1;
            const itemBorderRadius = isFirst
              ? { borderTopLeftRadius: 12, borderTopRightRadius: 12 }
              : isLast
                ? { borderBottomLeftRadius: 12, borderBottomRightRadius: 12 }
                : {};
            return (
              <Clickable
                key={index}
                onClick={() => {
                  option.onClick();
                  setOpen(false);
                }}
                hoverColor="blue-50"
                fullWidth
                style={itemBorderRadius}
              >
                <Layout paddingY={16} paddingX={12} {...option.itemProps}>
                  {typeof option.label === 'string' ? (
                    <Text size="body-xs" color={itemTextColor} wrap={false} align="left">
                      {option.label}
                    </Text>
                  ) : (
                    option.label
                  )}
                </Layout>
              </Clickable>
            );
          })}
        </Layout>
      )}
    </Layout>
  );
};

export default Menuable;
